// packages/engine/src/process-choice-coverage.ts
//
// P5 §11 — CHOICE-POINT coverage. Analogous to code coverage, but over a
// process's *decision* model rather than its steps: a well-covered process
// exercises each branch/try arm, each select run-set, each parallel branch +
// its merge-fail, and each loop boundary (0 / 1 / many). This is the metric
// that kills the "false 100%" a step-coverage report can show — step coverage
// hits 100% while never exercising a failure route or a merge outcome.
//
//   processChoiceSpace(process)  — the DENOMINATOR: every outcome cell the
//                                  process's choice points can take.
//   choiceCoverage(process, …)   — unions the cells a process's tests exercised
//                                  (EnumerateResult.coverageCells) and reports
//                                  covered / total + the uncovered cell list.
//
// A cell id is the bridge group nodeId (an arm/case/branch — a prefix of the
// steps that run under it), a `<loop>#0|1|many` bucket, or a `<parallel>#merge-
// fail`. Some cells are uncovered BY CONSTRUCTION (a `parallel select <count>`
// form has no pin yet, a static-count loop's other buckets are unreachable) —
// they surface in the uncovered list honestly rather than inflating the total.

import type { ProcessModel, ParallelNode } from "./process-model.js";
import type { WalkerContext } from "./process-walker-types.js";
import type { TestPin } from "./v010-ast.js";
import { buildChoiceBridge, enumerateTraces, loopBucket, type EnumerateOptions, type ChoicePoint } from "./process-trace-enumerator.js";

/** One outcome cell of a process's choice space. `id` is the stable cell key the
 *  per-test `coverageCells` set matches; `label` is a human hint for the
 *  uncovered list. */
export interface ChoiceCell {
  readonly id: string;
  readonly kind: "branch" | "try" | "select" | "parallel" | "loop" | "retry" | "merge-fail" | "parallel-select";
  readonly label: string;
  /** The choice container this cell belongs to (for grouping in a report). */
  readonly container: string;
}

/** The uncovered-by-construction buckets are still counted (honest); they can
 *  only be covered by an explicit pin (`count: 0` / `count: 1`) or not at all. */
const LOOP_BUCKETS = ["0", "1", "many"] as const;

/**
 * Enumerate the full choice space of `process`: every decision outcome cell.
 * Reuses {@link buildChoiceBridge} (the single source of truth for a process's
 * choice points) so the space and the per-test exercised set share one nodeId
 * scheme.
 */
export function processChoiceSpace(process: ProcessModel, ctx: WalkerContext): ChoiceCell[] {
  const bridge = buildChoiceBridge(process, ctx);
  const cells: ChoiceCell[] = [];
  const seen = new Set<string>();
  const add = (c: ChoiceCell): void => {
    if (seen.has(c.id)) return;
    seen.add(c.id);
    cells.push(c);
  };

  // Arm / case / branch cells — one per selectable group (its group nodeId is a
  // prefix of the steps that run under it).
  for (const p of bridge.points as readonly ChoicePoint[]) {
    if (!p.groupNodeId) continue;
    if (p.kind === "if") add({ id: p.groupNodeId, kind: "branch", label: p.raw === "else" ? "if → else" : `if "${p.raw}"`, container: p.containerNodeId ?? p.groupNodeId });
    else if (p.kind === "try") add({ id: p.groupNodeId, kind: "try", label: p.armLabel === "try" ? "try (no failure)" : `catch ${p.raw}`, container: p.containerNodeId ?? p.groupNodeId });
    else if (p.kind === "select") add({ id: p.groupNodeId, kind: "select", label: `select case ${p.raw}`, container: p.containerNodeId ?? p.groupNodeId });
    else if (p.kind === "parallel") add({ id: p.groupNodeId, kind: "parallel", label: `parallel branch ${p.raw}`, container: p.containerNodeId ?? p.groupNodeId });
    // A retry's arms are FAILURE-POINT cells: attempt-succeeds / catch (guarded
    // work failed) / else (retries exhausted).
    else if (p.kind === "retry") add({ id: p.groupNodeId, kind: "retry", label: p.armLabel === "try" ? "retry attempt (succeeds)" : p.armLabel === "else" ? "retry exhausted (else)" : p.raw === "catch" ? "retry catch" : `retry catch ${p.raw}`, container: p.containerNodeId ?? p.groupNodeId });
  }

  // Loop boundary buckets — 0 / 1 / many per plain `each` loop AND per backward
  // `go`-loop (its synthetic `goloop:<target>` container).
  for (const p of bridge.points as readonly ChoicePoint[]) {
    if ((p.kind !== "loop" && p.kind !== "goloop") || !p.containerNodeId) continue;
    const noun = p.kind === "goloop" ? "go-loop" : "loop";
    for (const b of LOOP_BUCKETS) add({ id: `${p.containerNodeId}#${b}`, kind: "loop", label: `${noun} "${p.raw}" ×${b}`, container: p.containerNodeId });
  }

  // Parallel merge-fail (a join / boolean / count / bare AND-join can fail its
  // merge over the pinned done-set) + the deferred `parallel select <count>`
  // form (uncovered by construction — no pin yet).
  for (const [nodeId, node] of bridge.parallels) {
    if (node.body.form === "select") {
      add({ id: `${nodeId}#parallel-select`, kind: "parallel-select", label: "parallel select <count> (uncovered by construction)", container: nodeId });
    }
    if (mergeCanFail(node)) add({ id: `${nodeId}#merge-fail`, kind: "merge-fail", label: "parallel merge fails", container: nodeId });
  }

  return cells;
}

/** A parallel whose merge evaluates over a done-set (so a completion pin can
 *  make it fail): any non-`race` parallel with at least one NAMED branch (an
 *  unnamed-branch parallel can't be pinned, so its merge is unreachable). */
function mergeCanFail(node: ParallelNode): boolean {
  if (node.merge.policy === "race" || node.awaitClause) return false;
  const branches = node.body.form === "branches" || node.body.form === "select" ? node.body.branches : [];
  return branches.some((b) => b.name?.name);
}

/** The coverage of a process's choice space by a set of tests. */
export interface ChoiceCoverage {
  readonly process: string;
  readonly covered: number;
  readonly total: number;
  readonly pct: number;
  /** The cells no test exercised — the AI's work list (design §11). */
  readonly uncovered: readonly ChoiceCell[];
}

/**
 * Compute choice-point coverage for one process over its tests. Each test's
 * pin-constrained trace-set exercises a set of cells (`EnumerateResult.
 * coverageCells`); the union across tests, intersected with the space, is the
 * covered set. Uncovered cells are the regression / work-list signal.
 */
export function choiceCoverage(
  process: ProcessModel,
  tests: readonly { readonly pins: readonly TestPin[] }[],
  ctx: WalkerContext,
  opts: EnumerateOptions = {},
): ChoiceCoverage {
  const space = processChoiceSpace(process, ctx);
  const exercised = new Set<string>();
  for (const test of tests) {
    const en = enumerateTraces(process, test.pins, ctx, opts);
    for (const c of en.coverageCells) exercised.add(c);
  }
  const uncovered = space.filter((c) => !exercised.has(c.id));
  const covered = space.length - uncovered.length;
  const total = space.length;
  return {
    process: process.name,
    covered,
    total,
    pct: total === 0 ? 100 : Math.round((covered / total) * 100),
    uncovered,
  };
}
